// CSS-text → React style object.
//
// WHY THIS EXISTS
// The design source (docs/discovery-calculator.dc.html) is a `.dc.html` prototype whose renderer
// accepts `style="…"` as CSS *text*, and whose render scope (`renderVals()`, ~1831-2257) builds
// most of its styles as strings — `style="{{ c.style }}"`, `style="{{ ln.cardStyle }}"`,
// `style="{{ spotStyle }}"`, … React's `style` prop takes an object instead, so every one of those
// strings has to be parsed somewhere.
//
// The port keeps the CSS text byte-identical to source (so the JSX stays diffable against the
// template, and renderVals() stays a near-verbatim transliteration) and converts at the JSX site:
//
//     style="display:flex;gap:10px"   →   style={sx('display:flex;gap:10px')}
//     style="{{ c.style }}"           →   style={sx(c.style)}

import type { CSSProperties } from 'react';

/**
 * Parsed-declaration cache. Almost every string handed to `sx()` is a literal from the template
 * (a bounded set), so the cache is a straight win; the handful of computed ones (the coach
 * spotlight rect, rebuilt every animation frame) would otherwise grow it without bound, hence the
 * cap-and-clear.
 */
const CACHE = new Map<string, CSSProperties>();
const CACHE_MAX = 4000;

/** kebab-case → camelCase, leaving custom properties (`--x`) alone. */
function propName(p: string): string {
  return p.startsWith('--') ? p : p.replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
}

/**
 * Parse a CSS declaration list into a React style object.
 *
 * Splits on `;` then on the first `:` of each declaration — safe for every value in the source
 * (no declaration contains a semicolon, and no value contains a colon). Values are passed through
 * as strings, which React accepts for any property.
 */
export function sx(css: string): CSSProperties {
  const hit = CACHE.get(css);
  if (hit) return hit;
  const out: Record<string, string> = {};
  for (const decl of css.split(';')) {
    const i = decl.indexOf(':');
    if (i < 0) continue;
    const prop = decl.slice(0, i).trim();
    if (!prop) continue;
    out[propName(prop)] = decl.slice(i + 1).trim();
  }
  const style = out as CSSProperties;
  if (CACHE.size >= CACHE_MAX) CACHE.clear();
  CACHE.set(css, style);
  return style;
}
